/**
 * Anlegen eines Belegs aus dem Anlagedialog — die Entscheidungen ohne React.
 *
 * Vorher stand alles in InvoicesView.handleCreateFromProject: welcher Weg gilt
 * (Abschlag, Teilrechnungen, Beleg mit Abzug, gewoehnliche Rechnung), was an
 * die Dienste geht und welcher Text danach im Dialog steht. Die Funktion hatte
 * keinen Test, und die Ansicht ist ohne DOM-Testumgebung nicht pruefbar (im
 * Projekt gibt es keine). Hier ist dieselbe Logik als reine Funktionen
 * gefasst.
 *
 * Zwei Schritte, weil die Ansicht zwischen ihnen etwas tut:
 *   1. prepareInvoiceCreate — rein und synchron. Ohne Kunden endet es hier,
 *      noch bevor die Zeile als "wird angelegt" markiert ist (setCreating).
 *   2. createInvoiceFromProject — ruft die Dienste und beschreibt, was die
 *      Ansicht danach tun muss. Die Dienste kommen als Objekt herein, damit
 *      Tests sie ersetzen koennen.
 *
 * Keiner der beiden Schritte fasst React-Zustand an. Die Ansicht wendet das
 * Ergebnis an, in derselben Reihenfolge der Setter wie zuvor.
 */
import type { Invoice, InvoiceConfig } from '../../types';
import {
    InvoiceValidationError,
    type buildProjectServiceLines,
    type createAdvanceInvoice,
    type createFinalInvoice,
    type createInvoiceDraft,
    type createSplitAdvanceInvoices,
    type createSplitInvoiceDrafts,
    type CreateInvoiceDraftInput,
    type CreateSplitDraftsInput,
    type ProjectServiceLinesOptions,
    type ProjectServiceLinesResult,
} from '../../services/invoiceService';
import type { fetchProjectSplit } from '../../services/projectSplitService';
import { hasSplit, type ProjectSplit } from '../../services/projectSplit';
import { modeFromStance, type BillingStance } from '../../services/billingMode';
import type { AdvanceAmountResult, PickerAnswers } from '../../services/invoicePickerFlow';
import type { AdvanceRequest } from '../../services/advanceInvoiceLogic';
import {
    computeOpenPositions,
    performedDetails,
    servicePeriodFromSelection,
    type UndatedSummary,
    type ValidationMessage,
} from '../../services/invoiceLogic';
import { getNormalizedServices, type ProjectRowLike } from '../../services/projectLv';
import { getServiceLog } from '../../services/serviceJournal';
import { formatCents, type Cents } from '../../services/money';
import { rechtFehlerOder } from '../../services/rechte/rechtFehler';
import { missingCustomerMessage } from '../project-hub/customerLinkLogic';
import type { CreatableDocumentType } from './invoicesViewShared';

// ─── Eingaben und Ergebnis ──────────────────────────────────────────────────

/** Belegarten mit Leistungspositionen — alles ausser dem Abschlag. */
export type ServiceDocumentType = Exclude<CreatableDocumentType, 'ADVANCE_INVOICE'>;

/**
 * Die Projektzeile, wie projectRows sie fuehrt — nur die Felder, die das
 * Anlegen liest. Das Projekt selbst ist in der Ansicht untypisiert; hier
 * steht, worauf sich das Anlegen tatsaechlich verlaesst.
 */
export interface InvoiceCreateEntry {
    project: ProjectRowLike & { id: string; customer_id?: string | null };
    netCents: Cents;
    contractedNetCents: Cents;
    undated: UndatedSummary;
}

/** Was die Ansicht beim Anlegen beisteuert — alles aus ihrem Zustand. */
export interface InvoiceCreateInput {
    entry: InvoiceCreateEntry;
    /**
     * Bereits auf den Plan der Kette beschnitten (effectivePickerAnswers).
     * Das Beschneiden bleibt in der Ansicht, weil es VOR dem Zuruecksetzen der
     * alten Meldung laeuft.
     */
    answers: PickerAnswers;
    /** Zustand des Projekts aus den eingefrorenen Fakten der Kette. */
    stance: BillingStance;
    /** Die Belegart des Dialogs (Zustand der Ansicht, nicht facts.docType). */
    docType: CreatableDocumentType;
    periodFrom: string;
    periodTo: string;
    includeUndated: boolean;
    /** Belegbestand des Anlagedialogs (Grundlage von projectRows). */
    pickerInvoices: Invoice[] | null;
    /** Die Belegliste der Ansicht — Quelle der laufenden Abschlagsnummer. */
    invoices: Invoice[];
    canAssignCustomer: boolean;
    invoiceConfig: InvoiceConfig;
    today: string;
}

/**
 * Alles, was nach der Vorpruefung feststeht. Die Dienstaufrufe lesen nur noch
 * von hier — keine zweite Ableitung aus dem Zustand der Ansicht.
 */
export interface InvoiceCreatePlan {
    projectId: string;
    projectName: string | undefined;
    customerId: string;
    docType: CreatableDocumentType;
    isCumulative: boolean;
    advance: AdvanceAmountResult | null;
    deductAdvances: boolean | null;
    /** Beginn des Belegs: vorgezogener Beginn (Vormonate) oder der gewaehlte. */
    effectiveFrom: string;
    periodTo: string;
    servicePeriod: { servicePeriodFrom?: string; servicePeriodTo?: string };
    includeUndated: boolean;
    /** Bemessungsgrundlage des Abschlags (nur von ADVANCE_INVOICE gelesen). */
    advanceBasisNetCents: Cents;
    invoices: Invoice[];
    /** Fuer den Hinweis bei einer Sperre durch Mengen ohne Protokolleintrag. */
    undated: UndatedSummary;
    invoiceConfig: InvoiceConfig;
    today: string;
}

export type InvoiceCreateErrorReason =
    | 'missing-customer'
    | 'no-advance-basis'
    | 'no-advance-amount'
    | 'nothing-to-bill'
    | 'validation'
    | 'failed';

/**
 * Kein Beleg. Die Ansicht setzt die Meldung und laesst Dialog und Kette
 * offen — genau wie die fruehen Ausstiege der alten Funktion.
 */
export interface InvoiceCreateError {
    kind: 'error';
    reason: InvoiceCreateErrorReason;
    error: string;
}

/** Welcher Weg den Beleg angelegt hat — fuer Tests und Fehlersuche. */
export type InvoiceCreatePath = 'advance' | 'split-advance' | 'split-invoice' | 'deducting' | 'plain';

/**
 * Beleg(e) angelegt. Die Ansicht schliesst den Dialog, stellt `drafts` vorn
 * in die Liste, oeffnet `open` im Editor und setzt `notice` — nur wenn es
 * nicht null ist; sonst bleibt der bisherige Hinweis unberuehrt.
 */
export interface InvoiceCreated {
    kind: 'created';
    path: InvoiceCreatePath;
    drafts: Invoice[];
    open: Invoice;
    notice: string | null;
}

export type InvoiceCreatePreparation = { kind: 'ready'; plan: InvoiceCreatePlan } | InvoiceCreateError;

export type InvoiceCreateOutcome = InvoiceCreated | InvoiceCreateError;

/**
 * Die Dienste, die das Anlegen ruft. Die Ansicht reicht die echten aus
 * invoiceService und projectSplitService herein, Tests ihre Doppel.
 */
export interface InvoiceCreateServices {
    fetchProjectSplit: typeof fetchProjectSplit;
    buildProjectServiceLines: typeof buildProjectServiceLines;
    createAdvanceInvoice: typeof createAdvanceInvoice;
    createSplitAdvanceInvoices: typeof createSplitAdvanceInvoices;
    createFinalInvoice: typeof createFinalInvoice;
    createInvoiceDraft: typeof createInvoiceDraft;
    createSplitInvoiceDrafts: typeof createSplitInvoiceDrafts;
}

type FinalInvoiceInput = Parameters<typeof createFinalInvoice>[0];
type AdvanceTally = Awaited<ReturnType<typeof createFinalInvoice>>['advances'];

// ─── Texte ──────────────────────────────────────────────────────────────────

export const noAdvanceBasisMessage = (projectName: string | undefined): string =>
    `Für "${projectName}" ist kein Auftragswert hinterlegt. Ein Abschlag braucht eine Bemessungsgrundlage — bitte im Leistungsverzeichnis die beauftragten Mengen und Einzelpreise erfassen.`;

export const MISSING_ADVANCE_AMOUNT_MESSAGE = 'Für den Abschlag fehlt die Höhe.';

export const nothingToBillMessage = (
    projectName: string | undefined,
    effectiveFrom: string,
    periodTo: string,
): string =>
    effectiveFrom || periodTo
        ? `Für "${projectName}" ist im gewählten Zeitraum nichts protokolliert. Entweder wurde dort nichts geleistet, oder die Mengen wurden ohne Eintrag im Leistungsprotokoll erfasst.`
        : `Für "${projectName}" ist nichts mehr offen — die Mengen sind bereits abgerechnet oder von einem anderen Entwurf beansprucht.`;

export const splitAdvanceNotice = (count: number): string =>
    `${count} Abschlagsrechnungen angelegt — je Beteiligtem eine, mit seinem Anteil.`;

export const splitInvoiceNotice = (count: number): string =>
    `${count} Teilrechnungen angelegt — je Beteiligtem eine (bei Abschnitten: je Haus eine). Sie gehören zusammen: fehlt eine davon, fordern sie in Summe zu wenig, und das Ausstellen der übrigen wird abgelehnt.`;

export const CREATE_FAILED_MESSAGE = 'Der Rechnungsentwurf konnte nicht angelegt werden.';

/**
 * Hinweis nach einem Beleg mit Abzug; null heisst: keiner.
 *
 * null und nicht der leere Text: die Ansicht setzt den Hinweis nur, wenn es
 * ueberhaupt einen gibt (frueher `if (hints.length)`), und ein Hinweisteil
 * kann fuer sich leer sein.
 */
export const deductionNotice = (
    isCumulative: boolean,
    advances: AdvanceTally,
    issues: ValidationMessage[],
): string | null => {
    // Wer den Abzug ausdruecklich verlangt und keinen bekommt, muss
    // erfahren warum. Sonst entsteht ein Beleg ohne Abzugsblock,
    // und der Unterschied faellt erst beim Kunden auf.
    // Wortwahl je Modell: kumulativ werden ALLE Vorbelege des
    // Projekts abgezogen, nicht nur Abschlagsrechnungen. Sie so zu
    // nennen liesse den Nutzer nach Abschlaegen suchen, die es gar
    // nicht geben muss.
    const what = isCumulative ? 'Beleg' : 'Abschlagsrechnung';
    const hints: string[] = [];
    if (advances.deductible === 0) {
        hints.push(
            advances.found === 0
                ? `Für dieses Projekt gibt es keine vorherigen ${isCumulative ? 'Belege' : 'Abschlagsrechnungen'} — es wurde nichts abgezogen.`
                : isCumulative
                  ? `${advances.found} vorherige Belege gefunden, aber keiner davon ist abzugsfähig. Abgezogen wird erst ab dem Ausstellen; Entwürfe und stornierte Belege zählen nicht.`
                  : `${advances.found} Abschlagsrechnung(en) gefunden, aber keine davon ist abzugsfähig. Abgezogen wird erst ab dem Ausstellen; ein Entwurf fordert noch kein Geld. Bereits verbrauchte Abschläge zählen ebenfalls nicht mehr.`,
        );
    } else if (advances.deductible < advances.found) {
        hints.push(
            `${advances.deductible} von ${advances.found} ${what}(en) abgezogen. Die übrigen sind noch nicht ausgestellt${isCumulative ? ' oder storniert' : ' oder bereits verbraucht'}.`,
        );
    }
    hints.push(...issues.map((i) => i.message));
    return hints.length ? hints.join(' ') : null;
};

/**
 * Die Meldung zu einem gescheiterten Anlegen.
 *
 * Gebraucht an zwei Stellen mit demselben Ergebnis: in
 * createInvoiceFromProject fuer die Dienstfehler und in der Ansicht als Netz
 * fuer das Anwenden des Ergebnisses — beides lag frueher in EINEM catch.
 */
export const describeCreateFailure = (
    e: unknown,
    undated: UndatedSummary,
): InvoiceCreateError => {
    // Die Validierung nennt ihre Gruende in issues; die Meldung der
    // Ausnahme selbst sagt nur DASS etwas fehlt. Wer hier nur message
    // anzeigt, laesst den Nutzer mit "Die Rechnung ist unvollstaendig"
    // allein und ohne jeden Hinweis, was zu tun waere.
    if (e instanceof InvoiceValidationError) {
        const reasons = e.issues
            .filter((i) => i.level === 'error')
            .map((i) => i.message)
            .join(' ');
        // Die beiden Sperren derselben Ursache: die Grundlage des
        // Belegs liegt unter dem, was die Vorbelege bereits gefordert
        // haben. Hat das Projekt Mengen ohne Protokolleintrag, ist der
        // Zeitraumfilter der wahrscheinliche Grund — er laesst genau
        // sie liegen. Der Dienst kann das nicht wissen (er sieht nur
        // Zeilen und Abzuege), hier ist die Zahl bekannt, und der Weg
        // dorthin steht im gelben Kasten ueber der Liste.
        const undatedBlocked =
            undated.count > 0 &&
            e.issues.some(
                (i) =>
                    i.code === 'advance_exceeds_rate_basis' ||
                    i.code === 'payable_negative',
            );
        const undatedHint = undatedBlocked
            ? ` In diesem Projekt sind ${formatCents(undated.netCents)} ohne Eintrag im Leistungsprotokoll erfasst — sie gehören zu keinem Monat und fallen durch den gewählten Zeitraum. Im gelben Kasten über der Projektliste lässt sich dafür ein Leistungsdatum nachtragen; danach zählen sie wieder mit.`
            : '';
        return {
            kind: 'error',
            reason: 'validation',
            error: `${`${e.message} ${reasons}`.trim()}${undatedHint}`,
        };
    }
    // Lesend wie frueher `e?.message` am untypisierten Fehler: auch ein
    // geworfener Text oder null landet beim Standardtext.
    const message = (e as { message?: string } | null | undefined)?.message;
    return {
        kind: 'error',
        reason: 'failed',
        error: rechtFehlerOder(e, () => message || CREATE_FAILED_MESSAGE),
    };
};

// ─── Entscheidungen ─────────────────────────────────────────────────────────

/**
 * Belegarten MIT Abschlagsabzug — sie laufen ueber createFinalInvoice.
 *
 * Fuer ADVANCE_INVOICE immer false; der Abschlag zweigt vorher ab.
 */
export const deductsAdvances = (
    docType: CreatableDocumentType,
    isCumulative: boolean,
    deductAdvances: boolean | null,
): boolean =>
    // ── Belege mit Abschlagsabzug ──────────────────────────────────
    // Schluss- und Restrechnung immer; die normale Rechnung kumulativ
    // (dort zieht der Beleg alle Vorbelege ab) oder auf ausdruecklichen
    // Wunsch — dann ist sie eine Teilleistungsrechnung.
    //
    // WARUM hier frueher `!cumulative && deductAdvances` stand: der
    // Abschlagsabzug durfte nur wirken, solange sein Haken auch SICHTBAR
    // war, denn mit gesetztem Haken „Kumulativ" lag er verborgen. Ohne
    // die Klammer haette ein frueher — womoeglich in einer anderen
    // Sitzung — gesetzter Abschlagsabzug unsichtbar weitergewirkt: ein
    // Abzugsblock, den niemand verlangt und niemand gesehen hat.
    //
    // Beide Bezugspunkte sind weg. Der Haken „Kumulativ" ist es schon
    // laenger, und `deductAdvances` ist kein gemerkter Dialogzustand
    // mehr, sondern die Antwort auf eine Frage, die genau dann gestellt
    // wird, wenn sie etwas bewirkt: gewoehnliche Rechnung, fortschreibend,
    // und das Projekt hat abzugsfaehige Abschlaege (invoicePickerFlow).
    // Was wirkt, ist damit wieder genau das, was der Nutzer eben
    // beantwortet hat — und das war der Zweck der Klammer. Sie ist
    // ersatzlos entfallen, nicht ersetzt: bei `isCumulative` greift
    // ohnehin der erste Teil der Oder-Bedingung.
    docType === 'FINAL_INVOICE' ||
    docType === 'REMAINING_INVOICE' ||
    (docType === 'INVOICE' && (isCumulative || deductAdvances === true));

/**
 * Laufende Nummer des naechsten Abschlags.
 */
export const nextAdvanceOrdinal = (invoices: Invoice[], projectId: string): number =>
    // Laufende Nummer aus den bereits vorhandenen Abschlaegen des
    // Projekts. Sie steht im Positionstext ("2. Abschlag") und macht
    // die Reihenfolge auf der Schlussrechnung nachvollziehbar.
    invoices.filter(
        (i) => i.documentType === 'ADVANCE_INVOICE' && i.primaryProjectId === projectId,
    ).length + 1;

// ─── Eingaben der Dienste ───────────────────────────────────────────────────

/** Die Anforderung des Abschlags — gleich fuer den geteilten und den einfachen Weg. */
export const advanceRequest = (
    plan: InvoiceCreatePlan,
    basis: Cents,
    amount: AdvanceAmountResult,
    ordinal: number,
): AdvanceRequest => ({
    basisNetCents: basis,
    percent: amount.percent,
    fixedNetCents: amount.fixedNetCents,
    taxRateBp: plan.invoiceConfig.defaultTaxRateBp,
    ordinal,
});

export const serviceLinesOptions = (plan: InvoiceCreatePlan): ProjectServiceLinesOptions => ({
    taxRateBp: plan.invoiceConfig.defaultTaxRateBp,
    periodFrom: plan.effectiveFrom || undefined,
    periodTo: plan.periodTo || undefined,
    includeUndated: plan.includeUndated,
    cumulative: plan.isCumulative,
});

export const splitDraftsInput = (
    plan: InvoiceCreatePlan,
    docType: ServiceDocumentType,
    built: ProjectServiceLinesResult,
    split: ProjectSplit,
    mitAbzug: boolean,
): CreateSplitDraftsInput => {
    const { isCumulative, effectiveFrom, periodTo, today, servicePeriod } = plan;
    return {
        base: {
            // Wird je Teilnehmer ersetzt; steht hier nur, weil
            // die Vorlage das Feld verlangt.
            customerId: plan.customerId,
            primaryProjectId: plan.projectId,
            documentType: mitAbzug ? docType : undefined,
            deliveryDateFrom: isCumulative
                ? built.cumulativeFrom
                : effectiveFrom || built.loggedFrom,
            deliveryDateTo: periodTo || built.loggedTo || today,
            lines: built.lines,
            details: {
                ...(mitAbzug ? {} : { orderReference: built.orderReference }),
                ...servicePeriod,
                ...performedDetails(built.performed),
                // Jeder Teilbeleg traegt dieselben Zeilen aus
                // DEMSELBEN Aufbau — und damit dieselbe Wahl.
                includeUndated: built.includeUndated,
            },
        },
        split,
        cumulative: isCumulative,
        deduction: mitAbzug ? { documentType: docType } : undefined,
    };
};

export const finalInvoiceInput = (
    plan: InvoiceCreatePlan,
    docType: ServiceDocumentType,
    built: ProjectServiceLinesResult,
): FinalInvoiceInput => {
    const { isCumulative, effectiveFrom, periodTo, today, servicePeriod } = plan;
    return {
        customerId: plan.customerId,
        primaryProjectId: plan.projectId,
        lines: built.lines,
        documentType: docType,
        cumulative: isCumulative,
        // Der gewaehlte Zeitraum hat Vorrang: er ist die Aussage
        // des Belegs. Nur ohne ihn wird der protokollierte
        // Zeitraum eingesetzt.
        //
        // Kumulativ gilt das NUR fuer das Ende: der gewaehlte
        // Monatsletzte ist der Stichtag, der Beleg rechnet aber
        // alles davor mit ab. Sein Beginn ist deshalb keine
        // Eingabe, sondern die erste Leistung bis zu diesem Tag —
        // ein von Hand gesetzter Monatserster deckte den Rest
        // nicht ab.
        deliveryDateFrom: isCumulative
            ? built.cumulativeFrom
            : effectiveFrom || built.loggedFrom,
        deliveryDateTo: periodTo || built.loggedTo || today,
        // Der Leistungszeitraum kommt aus dem Protokoll und
        // muss an den Beleg: aus ihm allein laesst er sich
        // spaeter nicht mehr gewinnen. Ebenso die Wahl „ohne
        // Datum mitnehmen", mit der die Zeilen entstanden.
        details: {
            ...servicePeriod,
            ...performedDetails(built.performed),
            includeUndated: built.includeUndated,
        },
    };
};

export const plainDraftInput = (
    plan: InvoiceCreatePlan,
    built: ProjectServiceLinesResult,
): CreateInvoiceDraftInput => ({
    customerId: plan.customerId,
    primaryProjectId: plan.projectId,
    deliveryDateFrom: plan.effectiveFrom || built.loggedFrom,
    deliveryDateTo: plan.periodTo || built.loggedTo || plan.today,
    lines: built.lines,
    details: {
        orderReference: built.orderReference,
        ...plan.servicePeriod,
        ...performedDetails(built.performed),
        includeUndated: built.includeUndated,
    },
});

// ─── Ablauf ─────────────────────────────────────────────────────────────────

/**
 * Schritt 1: alles, was ohne Dienst feststeht — rein und synchron.
 *
 * Die Ansicht ruft das NACH dem Zuruecksetzen der alten Meldung und VOR
 * setCreating; endet es mit einem Fehler, wird die Zeile nie als "wird
 * angelegt" markiert. Das war die Reihenfolge der alten Funktion.
 */
export const prepareInvoiceCreate = (input: InvoiceCreateInput): InvoiceCreatePreparation => {
    const { entry, answers, periodFrom, periodTo, includeUndated, pickerInvoices } = input;
    const p = entry.project;
    // Ab hier liest die ganze Funktion ausschliesslich `isCumulative`. Jede
    // uebersehene Stelle ergaebe einen widerspruechlichen Beleg —
    // Positionen nach dem einen Modell gebaut, der Abzug nach dem anderen.
    // Die Antwort der Kette gilt nur bei Zustand (c) — sonst steht das
    // Modell im Projekt. modeFromStance haelt diese Rangfolge an genau
    // einer Stelle (billingMode.ts).
    // (Seit der Auslagerung heisst "die ganze Funktion": dieser Schritt und
    // createInvoiceFromProject, verbunden ueber plan.isCumulative.)
    const isCumulative =
        modeFromStance(input.stance, answers.mode ?? 'PROGRESSIVE') === 'CUMULATIVE';
    const overrideFrom = answers.overrideFrom ?? undefined;
    // KEINE Ansage mehr nach dem Anlegen: der Zustand des Projekts steht
    // vor dem Klick an der Projektzeile, samt Betrag und Ausweg. Eine
    // Wiederholung danach traefe den Nutzer erst, wenn der Beleg schon da
    // ist, und stuende bei den rund 150 Bestandsprojekten an JEDER
    // Rechnung. Ueberstimmt wird ohnehin nichts mehr — ueberstimmt werden
    // konnte nur ein Haken, den es nicht mehr gibt.
    const effectiveFrom = overrideFrom || periodFrom;
    // Der Zeitraum, den der Beleg AUSWEIST — volle Monatsgrenzen der
    // Auswahl. Er wandert in details und nicht nach deliveryDateFrom/-To:
    // die beiden steuern, welche Protokolleintraege der Beleg uebernimmt,
    // und werden kumulativ zwingend bis zur ersten Leistung des Projekts
    // zurueckgezogen (built.cumulativeFrom). Genau daher stammte der
    // Andruck "01.03.2026 – 31.07.2026" auf einer Rechnung fuer Juni/Juli.
    //
    // Mit vorgezogenem Beginn (Vormonate einbezogen) gilt eben dieser: der
    // Beleg rechnet die Vormonate mit ab und muss sie ausweisen.
    const servicePeriod = servicePeriodFromSelection({
        from: effectiveFrom || undefined,
        to: periodTo || undefined,
    });
    // Mit vorgezogenem Beginn stimmen die Zahlen der Liste nicht mehr — sie
    // sind mit dem urspruenglichen Zeitraum gerechnet. Neu ermitteln, mit
    // denselben Argumenten wie in projectRows.
    const row =
        overrideFrom == null
            ? entry
            : (() => {
                  const services = getNormalizedServices(p);
                  const serviceLog = getServiceLog(p.data);
                  const positions = computeOpenPositions(services, new Map(), {
                      from: effectiveFrom || undefined,
                      to: periodTo || undefined,
                      serviceLog,
                      includeUndated,
                      // Derselbe Bestand wie in projectRows: der Aufruf
                      // rechnet dieselbe Zeile nur mit vorgezogenem Beginn
                      // neu, und der Eintrag stammt von dort — an dieser
                      // Stelle sind die Positionen also geladen.
                      invoices: (pickerInvoices ?? []).filter(
                          (i) => i.primaryProjectId === p.id,
                      ),
                  });
                  return {
                      ...entry,
                      positions,
                      netCents: positions.reduce((s, pos) => s + pos.netCents, 0),
                  };
              })();
    const customerId = p.customer_id;
    if (!customerId) {
        // Sollte die Kette nicht durchlassen — sie fragt den Kunden als
        // letzten Schritt ab. Bleibt als Netz stehen: wer nicht zuweisen
        // darf, kommt hier an und braucht die Begruendung.
        return {
            kind: 'error',
            reason: 'missing-customer',
            error: missingCustomerMessage(p.name, { canAssign: input.canAssignCustomer }),
        };
    }
    return {
        kind: 'ready',
        plan: {
            projectId: p.id,
            projectName: p.name,
            customerId,
            docType: input.docType,
            isCumulative,
            advance: answers.advance,
            deductAdvances: answers.deductAdvances,
            effectiveFrom,
            periodTo,
            servicePeriod,
            includeUndated,
            // Gelesen nur vom Abschlag; frueher dort aus `row` gebildet.
            advanceBasisNetCents: row.contractedNetCents || row.netCents,
            invoices: input.invoices,
            undated: entry.undated,
            invoiceConfig: input.invoiceConfig,
            today: input.today,
        },
    };
};

/**
 * Schritt 2: die Dienste rufen und beschreiben, was die Ansicht tun muss.
 *
 * Wirft nicht: jeder Fehler der Dienste wird hier zur Meldung
 * (describeCreateFailure), so wie frueher im catch der Ansicht.
 */
export const createInvoiceFromProject = async (
    plan: InvoiceCreatePlan,
    services: InvoiceCreateServices,
): Promise<InvoiceCreateOutcome> => {
    try {
        // `return await` und nicht `return`: nur so landet eine Ablehnung der
        // Dienste in diesem catch.
        return await runInvoiceCreate(plan, services);
    } catch (e: unknown) {
        return describeCreateFailure(e, plan.undated);
    }
};

const runInvoiceCreate = async (
    plan: InvoiceCreatePlan,
    services: InvoiceCreateServices,
): Promise<InvoiceCreateOutcome> => {
    // Als lokale Konstante, damit die Verengung nach dem Abschlagszweig fuer
    // den Rest gilt (docType ist danach ServiceDocumentType).
    const { docType, isCumulative, customerId, projectId, invoiceConfig, today } = plan;

    // ── Abschlag: Pauschale, keine LV-Positionen ────────────────────
    if (docType === 'ADVANCE_INVOICE') {
        const basis = plan.advanceBasisNetCents;
        if (basis <= 0) {
            return {
                kind: 'error',
                reason: 'no-advance-basis',
                error: noAdvanceBasisMessage(plan.projectName),
            };
        }
        // Geprueft ist die Hoehe bereits im Schritt „Höhe des
        // Abschlags" (readAdvanceAmount) — dort steht sie neben der
        // Bemessungsgrundlage und der Vorschau. Frueher fiel eine
        // unbrauchbare Eingabe erst hier auf, also nach dem Klick auf
        // ein Projekt, und die Meldung erschien hinter der Liste.
        const amount = plan.advance;
        if (!amount) {
            return {
                kind: 'error',
                reason: 'no-advance-amount',
                error: MISSING_ADVANCE_AMOUNT_MESSAGE,
            };
        }

        const ordinal = nextAdvanceOrdinal(plan.invoices, projectId);

        // Geteiltes Projekt: auch der Abschlag geht je Auftraggeber
        // heraus. Ein einziger Abschlagsbeleg an den bisherigen
        // Projektkunden forderte den vollen Betrag von einem, der nur
        // seinen Anteil schuldet — und die Schlussrechnung zoege ihn
        // spaeter bei allen ab.
        //
        // Geteilt wird hier der BETRAG und nicht die Menge: der
        // Abschlag traegt eine Pauschalposition ohne Bezug auf eine
        // LV-Position. Deshalb gilt die Grundquote, und deshalb wird
        // der Restcent nach dem Verfahren des groessten Restes vergeben
        // (splitCents) — die Summe der Teilbelege ist damit exakt der
        // angeforderte Abschlag.
        // Geteiltes Projekt: auch der Abschlag geht je Auftraggeber
        // heraus. Der Rechenweg liegt im Dienst, nicht hier — geteilt
        // wird der fertige Betrag, und die Gruppe entsteht ganz oder
        // gar nicht.
        const splitVorAbschlag = await services.fetchProjectSplit(projectId);
        if (hasSplit(splitVorAbschlag)) {
            const drafts = await services.createSplitAdvanceInvoices(
                {
                    primaryProjectId: projectId,
                    split: splitVorAbschlag!,
                    request: advanceRequest(plan, basis, amount, ordinal),
                },
                invoiceConfig,
                today,
            );
            return {
                kind: 'created',
                path: 'split-advance',
                drafts,
                open: drafts[0],
                notice: splitAdvanceNotice(drafts.length),
            };
        }

        const draft = await services.createAdvanceInvoice(
            {
                customerId,
                primaryProjectId: projectId,
                request: advanceRequest(plan, basis, amount, ordinal),
            },
            invoiceConfig,
            today,
        );
        return { kind: 'created', path: 'advance', drafts: [draft], open: draft, notice: null };
    }

    // Frisch vom Server, nicht aus der Liste: zwischen Laden und Klick
    // kann ein anderer Entwurf dieselben Mengen beansprucht haben.
    const built = await services.buildProjectServiceLines(projectId, serviceLinesOptions(plan));

    // Eine gewoehnliche Rechnung ohne Positionen ist ein leerer Beleg.
    // Bei Schluss- und Restrechnung ist das anders — dort kann die
    // Leistung vollstaendig durch Abschlaege abgedeckt sein.
    // Kumulativ ausgenommen: dort ist "nichts offen" der Normalfall —
    // der Beleg zeigt den Gesamtstand und zieht die Vorrechnungen ab.
    if (docType === 'INVOICE' && !isCumulative && built.lines.length === 0) {
        return {
            kind: 'error',
            reason: 'nothing-to-bill',
            error: nothingToBillMessage(plan.projectName, plan.effectiveFrom, plan.periodTo),
        };
    }

    // Belegarten MIT Abzug — dieselbe Bedingung wie unten, hier nur
    // einmal benannt, damit beide Wege sie nicht auseinanderentwickeln.
    // (Die Bedingung selbst und ihre Begruendung: deductsAdvances.)
    const mitAbzug = deductsAdvances(docType, isCumulative, plan.deductAdvances);

    // ── Teilt sich das Projekt auf mehrere Auftraggeber? ───────────
    //
    // Dann entsteht KEIN Beleg, sondern einer je Auftraggeber. Ein
    // Beleg mit mehreren Empfaengern waere keiner: jeder braucht eigene
    // Nummer, eigenen Kundenschnappschuss und eigenen Debitor.
    //
    // Die Abfrage steht VOR beiden Wegen, weil die Aufteilung beide
    // betrifft — mit Abzug laeuft jeder Teilbeleg ueber
    // createFinalInvoice und zieht dabei nur die Vorrechnungen SEINES
    // Auftraggebers ab.
    const split = await services.fetchProjectSplit(projectId);
    if (hasSplit(split)) {
        const drafts = await services.createSplitInvoiceDrafts(
            splitDraftsInput(plan, docType, built, split!, mitAbzug),
            invoiceConfig,
            today,
        );
        return {
            kind: 'created',
            path: 'split-invoice',
            drafts,
            open: drafts[0],
            notice: splitInvoiceNotice(drafts.length),
        };
    }

    if (mitAbzug) {
        // Bewusst KEINE Sperre bei null Positionen: eine
        // Schlussrechnung, deren Leistung vollstaendig durch Abschlaege
        // abgedeckt ist, ist ein regulaerer Fall. Ob der Beleg tragfaehig
        // ist, entscheidet createFinalInvoice anhand der Abschlagskette.
        const { invoice: draft, issues, advances } = await services.createFinalInvoice(
            finalInvoiceInput(plan, docType, built),
            invoiceConfig,
            today,
        );
        return {
            kind: 'created',
            path: 'deducting',
            drafts: [draft],
            open: draft,
            notice: deductionNotice(isCumulative, advances, issues),
        };
    }

    // ── Normale Rechnung ohne Abschlagsabzug ───────────────────────
    // Die Pruefung auf leere Positionen steht weiter oben, vor der
    // Verzweigung: sie gilt fuer beide Wege der Belegart INVOICE.
    const draft = await services.createInvoiceDraft(
        plainDraftInput(plan, built),
        invoiceConfig,
        today,
    );
    return { kind: 'created', path: 'plain', drafts: [draft], open: draft, notice: null };
};
